// -----------------------------------------------------------------------------
// <copyright file="edialog.service.ts" company="ecoInsight, Inc.">
// 	Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import { ComponentRef, Injectable, ViewContainerRef, signal, WritableSignal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GlobalService } from '../services/global.service';

import { EDialogAction, EDialogConf } from './edialog.model';
// eslint-disable-next-line import/order
import { EDialogComponent } from './edialog/edialog.component';

class EDialogInfo {
  popup!: ComponentRef<any>;
  child!: ComponentRef<any>;
  modal!: HTMLElement;
  title!: HTMLElement;
  content!: HTMLElement;
}

@Injectable({
  providedIn: 'root',
})
export class EDialogService {
  private bodyHtml: HTMLElement | null = null;
  private dialogQueue: EDialogInfo[] = [];
  private viewContainerRef: ViewContainerRef | null = null;
  // isKeyPressed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  data: any;

  constructor(private globalService: GlobalService) {}

  public init(viewContainerRef?: ViewContainerRef): void {
    this.viewContainerRef = null;
    if (viewContainerRef) {
      this.viewContainerRef = viewContainerRef;
    }
  }

  public createDialog(dialog: EDialogConf): EDialogAction {
    const dialogInfo = new EDialogInfo();
    if (!this.viewContainerRef) {
      throw new Error('ViewContainerRef is not initialized. Call init() first.');
    }
    const dialogRef = this.viewContainerRef.createComponent(EDialogComponent);
    if (this.bodyHtml === null) {
      this.bodyHtml = document.body as HTMLElement;
    }

    dialogInfo.popup = dialogRef;
    dialogInfo.popup.instance.id = dialog.id || dialog.component.name;
    dialogInfo.popup.instance.title = dialog.title;
    if (dialog.showCloseButton !== undefined) {
      dialogInfo.popup.instance.showCloseButton = dialog.showCloseButton;
    }
    this.findPopupWrapperAndContent(dialogInfo.popup.location.nativeElement, dialogInfo);
    this.removeElement(dialogInfo.popup.location.nativeElement);
    this.bodyHtml.appendChild(dialogInfo.popup.location.nativeElement);
    const componentRef = this.viewContainerRef.createComponent(
      dialog.component,
    ) as ComponentRef<any>;
    dialogInfo.child = componentRef;
    if (dialog.input) {
      componentRef.instance.data = dialog.input;
    }
    this.removeElement(componentRef.location.nativeElement);
    dialogInfo.content.appendChild(componentRef.location.nativeElement);
    this.dialogQueue.push(dialogInfo);
    const response: EDialogAction = {
      close: this.close(dialog.id),
      action: this.action(dialogRef, componentRef),
      send: (action: string, message?: any) => componentRef.instance.onMessage(action, message),
    };
    if (document.getElementsByClassName('edialog').length >= 1) {
      this.globalService.showOverlayInDialog.next(true);
    }

    return response;
  }

  private close(id: string): any {
    return () => {
      this.closed(id);
      // this.isKeyPressed$.next(false);
    };
  }

  private action(
    popup: ComponentRef<any>,
    child: ComponentRef<any>,
  ): Observable<{ action: string; payload?: any }> {
    const unsubscribe = new Subject<void>();
    return new Observable((observer) => {
      if (popup.instance.action) {
        popup.instance.action
          .pipe(takeUntil(unsubscribe))
          .subscribe((action: { action: string; payload?: any }) => {
            observer.next(action);
            if (action.action === 'CLOSE') {
              this.closed(popup.instance.id);
              unsubscribe.next();
              unsubscribe.complete();
            }
          });
      }
      if (child.instance.action) {
        child.instance.action
          .pipe(takeUntil(unsubscribe))
          .subscribe((action: { action: string; payload?: any }) => {
            observer.next(action);
            if (action.action === 'CLOSE') {
              this.closed(popup.instance.id);
              unsubscribe.next();
              unsubscribe.complete();
            }
          });
      }
      popup.instance.unsubscribe = unsubscribe;
    });
  }

  private closed(id: string): any {
    this.dialogQueue = this.dialogQueue.filter((d) => {
      if (d.popup.instance.id === id) {
        if (d.popup.instance.unsubscribe) {
          d.popup.instance.unsubscribe.next();
          d.popup.instance.unsubscribe.complete();
        }
        d.child.destroy();
        d.popup.destroy();
        // this.isKeyPressed$.next(false);
        this.removeElement(d.popup.location.nativeElement);
        return false;
      } else {
        return true;
      }
    });
  }

  private findPopupWrapperAndContent(popup: HTMLElement, dialogInfo: EDialogInfo): void {
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < popup.children.length; ++i) {
      const c = popup.children[i] as HTMLElement;
      if (c != null) {
        switch (c.className) {
          case 'dialog':
            dialogInfo.modal = c;
            break;
          // case "title":
          //   dialogInfo.title = c;
          //   break;
          case 'content':
            dialogInfo.content = c;
            break;
        }
        this.findPopupWrapperAndContent(c, dialogInfo);
      }
    }
  }

  private removeElement(el: HTMLElement): void {
    if (el && el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }
}
